import { Card } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FilePicker } from './file-picker';
import { FileInfo, FileInfoInput } from '../../../../../../generated';
import FileSelectionCard from '../../../../../shared/file-selection';

interface VerificationComponentPacketDocumentPickerProps {
  handlePdfMerge: (blobPaths: FileInfoInput[]) => void;
  blobUrls: FileInfo[];
  verificationFileNames: Map<string, string>;
  isProcessingMergingPdfs: boolean;
  onCancel: () => void;
}

export interface SelectedFileInfo {
  name: string;
  url: string;
  type: string;
  caseType: string;
  caseId: string;
}

interface SelectedFilesMap {
  [key: string]: SelectedFileInfo | null;
}

export const VerificationComponentPacketDocumentPicker: React.FC<VerificationComponentPacketDocumentPickerProps> = (props) => {
  const initialFiles = {
    credential: {
      name: props.blobUrls[0]?.name ?? '',
      url: props.blobUrls[0]?.url || '',
      type: props.blobUrls[0].type || 'application/pdf',
      caseType: '',
      caseId: ''
    },
    translation: {
      name: props.blobUrls[1]?.name ?? '',
      url: props.blobUrls[1]?.url || '',
      type: props.blobUrls[1]?.type || 'application/pdf',
      caseType: '',
      caseId: ''
    },
    'verification-packet-response': {
      name: props.blobUrls[2]?.name ?? '',
      url: props.blobUrls[2]?.url || '',
      type: props.blobUrls[2]?.type || 'application/pdf',
      caseType: '',
      caseId: ''
    }
  };

  function useFileSelection(initialFiles: SelectedFilesMap) {
    const [selectedFilesMap, setSelectedFilesMap] = useState<SelectedFilesMap>(initialFiles);
    const [currentFileSelection, setCurrentFileSelection] = useState<string>('');

    const handleFileSelection = (fileInfo: SelectedFileInfo) => {
      setSelectedFilesMap((prevFilesMap) => ({
        ...prevFilesMap,
        [currentFileSelectionRef.current]: fileInfo
      }));
    };

    const onCancel = () => {
      props.onCancel();
      setCurrentFileSelection('');
      setSelectedFilesMap(initialFiles);
    };

    return {
      selectedFilesMap,
      currentFileSelection,
      setCurrentFileSelection,
      handleFileSelection,
      onCancel
    };
  }

  const { selectedFilesMap, currentFileSelection, setCurrentFileSelection, handleFileSelection, onCancel } = useFileSelection(initialFiles);

  const onGenerateDocument = () => {
    const requiredFiles = ['credential', 'verification-packet-response'];

    const allFilesPresent = requiredFiles.every((file) => selectedFilesMap[file]);
    if (!allFilesPresent) {
      return;
    }
    const blobPaths: FileInfoInput[] = [];
    for (const key in selectedFilesMap) {
      const fileInfo = selectedFilesMap[key];
      if (fileInfo) {
        const blobInfo: FileInfoInput = {
          fileName: fileInfo.name,
          fileType : fileInfo.type
        }
        blobPaths.push(blobInfo);
      }

    }
    props.handlePdfMerge(blobPaths);
  };

  const files = [
    {
      type: 'credential',
      label: 'Select',
      fileName: selectedFilesMap.credential?.name || 'credential.pdf'
    },
    {
      type: 'translation',
      label: 'Select',
      fileName: selectedFilesMap.translation?.name || 'translation.pdf'
    },
    {
      type: 'verification-packet-response',
      label: 'Select',
      fileName: selectedFilesMap['verification-packet-response']?.name || 'verification-packet-response.pdf'
    }
  ];

  const currentFileSelectionRef = useRef(currentFileSelection);
  useEffect(() => {
    currentFileSelectionRef.current = currentFileSelection;
  }, [currentFileSelection]);

  const currentFilePicker = `File Picker - ${currentFileSelection}`;
  return (
    <div
      style={{
        display: 'flex'
      }}
    >
      <FileSelectionCard
        title={"Generate Verification Component Packet"}
        isProcessingMergingPdfs={props.isProcessingMergingPdfs}
        files={files}
        selectedFilesMap={selectedFilesMap}
        onFileSelect={setCurrentFileSelection}
        currentFileSelection={currentFileSelection}
        onCancel={onCancel}
        onGenerateDocument={onGenerateDocument}
      />
      {currentFileSelection !== '' && (
        <Card title={currentFilePicker}>
          <FilePicker handleFileSelection={handleFileSelection} />
        </Card>
      )}
    </div>
  );
};
